import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const LAUNCH_YEAR = 2026;

export default function Footer() {
  const year = new Date().getFullYear();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  return (
    <footer
      className="border-t border-white/10 bg-[#050505]"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Link
              to="/"
              className="font-display font-black text-2xl tracking-tighter inline-block"
              data-testid="footer-logo"
            >
              PIVOT<span className="text-[#E5FE40]">PROOF</span>.
            </Link>
            <p className="mt-3 text-sm text-white/55 max-w-sm leading-relaxed">
              Five hostile experts read your startup idea and tell you the
              truth. Built to prevent founders from building the wrong thing.
            </p>
            <a
              href="mailto:info@pivotproof.org"
              data-testid="footer-email"
              className="mt-5 inline-block font-mono text-xs text-[#E5FE40] hover:text-white tracking-wide border-b border-[#E5FE40]/30 hover:border-white pb-0.5"
            >
              info@pivotproof.org
            </a>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
              Product
            </div>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/pricing" data-testid="footer-pricing" className="text-white/70 hover:text-[#E5FE40] transition-colors">Pricing</Link>
              </li>
              <li>
                <Link to="/panel" data-testid="footer-panel" className="text-white/70 hover:text-[#E5FE40] transition-colors">Meet the Panel</Link>
              </li>
              <li>
                <Link to="/hall" data-testid="footer-hall" className="text-white/70 hover:text-[#E5FE40] transition-colors">Hall of Verdicts</Link>
              </li>
              <li>
                <Link to="/blog" data-testid="footer-blog" className="text-white/70 hover:text-[#E5FE40] transition-colors">Blog</Link>
              </li>
              <li>
                <Link to="/validate-startup-idea" data-testid="footer-validate" className="text-white/70 hover:text-[#E5FE40] transition-colors">Validate a startup idea</Link>
              </li>
              <li>
                <Link to="/startup-idea-testing" data-testid="footer-testing" className="text-white/70 hover:text-[#E5FE40] transition-colors">Startup idea testing</Link>
              </li>
              <li>
                <Link to="/digital-shark-tank" data-testid="footer-shark-tank" className="text-white/70 hover:text-[#E5FE40] transition-colors">Digital shark tank</Link>
              </li>
              <li>
                <Link to="/ideas" data-testid="footer-ideas" className="text-white/70 hover:text-[#E5FE40] transition-colors">Idea verdicts</Link>
              </li>
              <li>
                <Link to="/faq" data-testid="footer-faq" className="text-white/70 hover:text-[#E5FE40] transition-colors">FAQ</Link>
              </li>
              <li>
                <Link to="/about" data-testid="footer-about" className="text-white/70 hover:text-[#E5FE40] transition-colors">About</Link>
              </li>
              <li>
                <Link to="/receipts" data-testid="footer-receipts" className="text-white/70 hover:text-[#E5FE40] transition-colors">Receipts</Link>
              </li>
              <li>
                <Link to="/affiliates" data-testid="footer-affiliates" className="text-white/70 hover:text-[#E5FE40] transition-colors">Affiliates — earn 30%</Link>
              </li>
              <li>
                <Link to="/feedback" data-testid="footer-feedback" className="text-white/70 hover:text-[#E5FE40] transition-colors">Feedback</Link>
              </li>
              {isAdmin && (
                <li>
                  <Link to="/admin/affiliates" data-testid="footer-admin-affiliates" className="text-white/70 hover:text-[#E5FE40] transition-colors">Affiliate ledger</Link>
                </li>
              )}
              {isAdmin && (
                <li>
                  <Link to="/admin/launch-kit" data-testid="footer-launch-kit" className="text-white/70 hover:text-[#E5FE40] transition-colors">Launch kit</Link>
                </li>
              )}
              {isAdmin && (
                <li>
                  <Link to="/studio" data-testid="footer-studio" className="text-white/70 hover:text-[#E5FE40] transition-colors">Content Studio</Link>
                </li>
              )}
              {isAdmin && (
                <li>
                  <Link to="/brand-guide" data-testid="footer-brand-guide" className="text-white/70 hover:text-[#E5FE40] transition-colors">Brand guide</Link>
                </li>
              )}
              {isAdmin && (
                <li>
                  <Link to="/status" data-testid="footer-status" className="text-white/70 hover:text-[#E5FE40] transition-colors">System Status</Link>
                </li>
              )}
            </ul>
          </div>

          <div className="md:col-span-3" data-testid="footer-compare">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
              Compare
            </div>
            <ul className="space-y-2.5 text-sm">
              {[
                ["chatgpt", "vs asking ChatGPT"],
                ["validatorai", "vs ValidatorAI"],
                ["lean-canvas", "vs Lean Canvas"],
                ["customer-interviews", "vs customer interviews"],
                ["startup-advisor", "vs a startup advisor"],
              ].map(([slug, label]) => (
                <li key={slug}>
                  <Link to={`/vs/${slug}`} data-testid={`footer-vs-${slug}`} className="text-white/70 hover:text-[#E5FE40] transition-colors">{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
              Legal
            </div>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link
                  to="/privacy"
                  data-testid="footer-privacy"
                  className="text-white/70 hover:text-[#E5FE40] transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  data-testid="footer-terms"
                  className="text-white/70 hover:text-[#E5FE40] transition-colors"
                >
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <Link
          to="/affiliates"
          data-testid="footer-affiliate-cta"
          className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-[#E5FE40]/40 bg-[#E5FE40]/[0.05] hover:bg-[#E5FE40]/[0.1] px-5 py-4 transition-colors group"
        >
          <div>
            <div className="font-display font-bold text-base md:text-lg tracking-tight">
              Have an audience of founders? <span className="text-[#E5FE40]">Earn 30% on every report you refer.</span>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/45 mt-1">
              Self-serve link · 12-month customer window · monthly PayPal payouts
            </div>
          </div>
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#E5FE40] whitespace-nowrap group-hover:translate-x-1 transition-transform">
            Become an affiliate →
          </span>
        </Link>

        <div className="mt-8 pt-6 border-t border-white/5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">
            © {LAUNCH_YEAR}
            {year !== LAUNCH_YEAR ? `–${year}` : ""} PivotProof. All rights reserved.
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">
            Built for founders. Powered by hostile honesty.
          </div>
        </div>
      </div>
    </footer>
  );
}
